import { useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, Heading, HStack, Stack, Text } from '@chakra-ui/react'
import { LuChevronLeft, LuHistory, LuLandmark, LuUsers } from 'react-icons/lu'
import type {
  CapacityView,
  DetailContext,
  SibCapacityRow,
  BankCapacityRow,
} from './capacity.types'
import { SibCapacityTable } from './components/SibCapacityTable'
import { BankCapacityTable } from './components/BankCapacityTable'
import { SibDetailPanel } from './components/SibDetailPanel'
import { BankDetailPanel } from './components/BankDetailPanel'
import { UnionChangeTable } from './components/UnionChangeTable'
import {
  getCapacitySibsPage,
  getCapacityBanksPage,
  adaptSibItem,
  adaptBankItem,
} from '../../../api/capacity.api'
import { getOrders } from '../../../api/orders.api'
import { getPlanningYears } from '../../../api/planning.api'

export function CapacityManagementDashboard() {
  const [view, setView] = useState<CapacityView>('sibs')
  const [detail, setDetail] = useState<DetailContext>(null)
  const openSibDetail = (userId: string) => {
    const sib = sibRows.find(row => row.userId === userId)

    setDetail({
      kind: 'sib',
      userId,
      title: sib?.sib ?? 'SIB',
    })
  }

  const [sibRows, setSibRows] = useState<SibCapacityRow[]>([])
  const [bankRows, setBankRows] = useState<BankCapacityRow[]>([])
  const [sibsLoading, setSibsLoading] = useState(true)
  const [sibsLoadingMore, setSibsLoadingMore] = useState(false)
  const [sibsTotalCount, setSibsTotalCount] = useState(0)
  const [banksLoading, setBanksLoading] = useState(false)
  const [banksStarted, setBanksStarted] = useState(false)
  const [banksLoadingMore, setBanksLoadingMore] = useState(false)
  const [banksTotalCount, setBanksTotalCount] = useState(0)
  const [sibsError, setSibsError] = useState<string | null>(null)
  const [banksError, setBanksError] = useState<string | null>(null)
  const [submittedCustomerIds, setSubmittedCustomerIds] = useState<Set<number>>(new Set())
  // The year the bank list was last loaded for; undefined until the bank tab first opens.
  const banksLoadedFor = useRef<number | null | undefined>(undefined)

  // SIB-928: once Union opens the next year, the capacities can be viewed per open year.
  // null means "the running year" and sends no year, exactly as before.
  const [openYears, setOpenYears] = useState<number[]>([])
  const [currentYear, setCurrentYear] = useState<number | null>(null)
  const [year, setYear] = useState<number | null>(null)

  useEffect(() => {
    getPlanningYears()
      .then(res => {
        setCurrentYear(res.currentYear)
        setOpenYears([...(res.openYears ?? [])].sort((a, b) => a - b))
      })
      .catch(() => {
        /* without planning years the dashboard simply shows the running year */
      })
  }, [])

  const requestYear = year ?? undefined
  // The change list is mounted the first time its tab opens and stays mounted afterwards, so
  // its filters and page survive switching tabs, like the SIB and bank tables.
  const [changesStarted, setChangesStarted] = useState(false)

  useEffect(() => {
    if (view === 'changes') setChangesStarted(true)
  }, [view])

  useEffect(() => {
    setSibsLoading(true)
    setSibsError(null)
    setSibRows([])
    let cancelled = false

    async function loadSibs() {
      try {
        const first = await getCapacitySibsPage(1, requestYear)
        if (cancelled) return
        setSibRows(first.items.map(adaptSibItem))
        setSibsTotalCount(first.totalElements)
        setSibsLoading(false)

        if (first.totalPages > 1) {
          setSibsLoadingMore(true)
          for (let p = 2; p <= first.totalPages; p++) {
            if (cancelled) return
            const page = await getCapacitySibsPage(p, requestYear)
            if (cancelled) return
            setSibRows(prev => [...prev, ...page.items.map(adaptSibItem)])
          }
          if (!cancelled) setSibsLoadingMore(false)
        }
      } catch (e: any) {
        if (!cancelled) {
          setSibsError(e?.message || 'Fehler beim Laden der SIB-Daten')
          setSibsLoading(false)
          setSibsLoadingMore(false)
        }
      }
    }

    loadSibs()
    return () => {
      cancelled = true
    }
  }, [requestYear])

  useEffect(() => {
    getOrders({ status: 'SUBMITTED' })
      .then(res => {
        const ids = new Set<number>()
        for (const byUser of Object.values(res.items)) {
          for (const orders of Object.values(byUser)) {
            for (const order of orders) {
              if (order.customerId != null) ids.add(order.customerId)
            }
          }
        }
        setSubmittedCustomerIds(ids)
      })
      .catch(() => {
        /* silently ignore — alert simply won't show */
      })
  }, [])

  useEffect(() => {
    if (view !== 'banks' || banksLoadedFor.current === year) return
    banksLoadedFor.current = year
    setBanksStarted(true)
    setBanksLoading(true)
    setBanksError(null)
    setBankRows([])

    let cancelled = false
    let finished = false

    async function loadBanks() {
      try {
        const first = await getCapacityBanksPage(1, requestYear)
        if (cancelled) return
        setBankRows(first.items.map(adaptBankItem))
        setBanksTotalCount(first.totalElements)
        setBanksLoading(false)

        if (first.totalPages > 1) {
          setBanksLoadingMore(true)
          for (let p = 2; p <= first.totalPages; p++) {
            if (cancelled) return
            const page = await getCapacityBanksPage(p, requestYear)
            if (cancelled) return
            setBankRows(prev => [...prev, ...page.items.map(adaptBankItem)])
          }
          if (!cancelled) setBanksLoadingMore(false)
        }
        finished = true
      } catch (e: any) {
        finished = true
        if (!cancelled) {
          setBanksError(e?.message || 'Fehler beim Laden der Bankdaten')
          setBanksLoading(false)
          setBanksLoadingMore(false)
        }
      }
    }

    loadBanks()
    return () => {
      cancelled = true
      // a load cut off by a year switch must run again the next time the bank tab shows it
      if (!finished && banksLoadedFor.current === year) banksLoadedFor.current = undefined
    }
  }, [view, year, requestYear])

  function handleOpenSib(userId: string) {
    const sibRow = sibRows.find(r => r.userId === userId)
    if (sibRow) setDetail({ kind: 'sib', title: sibRow.sib, userId })
  }

  function handleOpenBank(customerId: number) {
    const bankRow = bankRows.find(r => r.customerId === customerId)
    if (bankRow) setDetail({ kind: 'bank', title: bankRow.bank, customerId })
  }

  const shownYear = year ?? currentYear

  const yearTabs =
    openYears.length > 1 && shownYear !== null ? (
      <HStack
        gap={1}
        borderWidth="1px"
        borderColor="border"
        borderRadius="lg"
        p="1"
        role="group"
        aria-label="Planungsjahr"
      >
        {openYears.map(y => {
          const active = shownYear === y
          return (
            <Button
              key={y}
              size="sm"
              variant={active ? 'subtle' : 'ghost'}
              colorPalette={active ? 'blue' : 'gray'}
              aria-pressed={active}
              onClick={() => setYear(y === currentYear ? null : y)}
            >
              {y}
            </Button>
          )
        })}
      </HStack>
    ) : null

  // Scope tabs styled like the Bestellübersicht toolbar; rendered inside each
  // table's toolbar row so search/sort/filter state can stay per table.
  const scopeTabs = (
    <HStack gap={1} borderWidth="1px" borderColor="border" borderRadius="lg" p="1">
      {(
        [
          { key: 'sibs', label: 'SIB-Kapazitäten', icon: LuUsers },
          { key: 'banks', label: 'Banken', icon: LuLandmark },
          { key: 'changes', label: 'Änderungen', icon: LuHistory },
        ] as const
      ).map(tab => {
        const active = view === tab.key
        return (
          <Button
            key={tab.key}
            size="sm"
            variant={active ? 'subtle' : 'ghost'}
            colorPalette={active ? 'blue' : 'gray'}
            onClick={() => setView(tab.key)}
          >
            <HStack gap={2}>
              <tab.icon />
              <Text>{tab.label}</Text>
            </HStack>
          </Button>
        )
      })}
    </HStack>
  )

  const viewTabs = yearTabs ? (
    <HStack gap={2} flexWrap="wrap">
      {yearTabs}
      {scopeTabs}
    </HStack>
  ) : (
    scopeTabs
  )

  return (
    <Box w="full" p={4}>
      {/* Detail view — mounts/unmounts normally; state we care about is in the list below */}
      {detail && (
        <Stack gap={6}>
          <HStack gap={3} align="center">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDetail(null)}
              borderColor="border"
            >
              <LuChevronLeft />
              Zurück
            </Button>
            <Text fontSize="sm" color="fg.subtle">
              {detail.title}
            </Text>
          </HStack>
          {detail.kind === 'sib' ? (
            <SibDetailPanel userId={detail.userId} bankRows={bankRows} year={requestYear} />
          ) : (
            <BankDetailPanel
              customerId={detail.customerId}
              year={requestYear}
              sibRows={sibRows}
              submittedCustomerIds={submittedCustomerIds}
              onOpenSibDetail={openSibDetail}
            />
          )}
        </Stack>
      )}

      {/* List view — always mounted so filter/sort state survives navigation */}
      <Box data-testid="capacity-management-dashboard" display={detail ? 'none' : undefined}>
        <Heading size="lg" fontWeight="normal" color="fg" mb={4}>
          Kapazitätssteuerung
        </Heading>

        {/* Both tab bodies stay mounted; only visibility changes */}
        <Box display={view === 'sibs' ? undefined : 'none'}>
          {sibsError ? (
            <Stack gap={4}>
              <HStack justify="flex-end">{viewTabs}</HStack>
              <Alert.Root status="error" variant="subtle">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Title>Fehler</Alert.Title>
                  <Alert.Description>{sibsError}</Alert.Description>
                </Alert.Content>
              </Alert.Root>
            </Stack>
          ) : (
            <SibCapacityTable
              rows={sibRows}
              onOpenDetail={handleOpenSib}
              isLoading={sibsLoading}
              isLoadingMore={sibsLoadingMore}
              totalCount={sibsTotalCount}
              tabs={viewTabs}
            />
          )}
        </Box>

        <Box display={view === 'banks' ? undefined : 'none'}>
          {banksError ? (
            <Stack gap={4}>
              <HStack justify="flex-end">{viewTabs}</HStack>
              <Alert.Root status="error" variant="subtle">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Title>Fehler</Alert.Title>
                  <Alert.Description>{banksError}</Alert.Description>
                </Alert.Content>
              </Alert.Root>
            </Stack>
          ) : (
            <BankCapacityTable
              rows={bankRows}
              onOpenDetail={handleOpenBank}
              isLoading={!banksStarted || banksLoading}
              isLoadingMore={banksLoadingMore}
              totalCount={banksTotalCount}
              tabs={viewTabs}
            />
          )}
        </Box>

        <Box display={view === 'changes' ? undefined : 'none'}>
          {changesStarted && (
            <UnionChangeTable tabs={viewTabs} enabled={view === 'changes' && !detail} />
          )}
        </Box>
      </Box>
    </Box>
  )
}
